import React from 'react'
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid'
import { Header2, Header3, PSmallFaded, PMedium } from './Type'
import { Card } from './Cards'

interface LoadingProps {
  label?: string
  message?: string
  size?: 'small' | 'medium' | 'large'
  inline?: boolean
}

interface ErrorProps {
  title?: string
  message?: string
  onRetry?: () => void
  retryText?: string
  showRetry?: boolean
  actionable?: boolean
}

export const LoadingSmall: React.FC<LoadingProps> = ({ label, inline = false }) => {
  const containerClass = inline
    ? 'inline-flex items-center gap-2'
    : 'flex justify-center items-center gap-2'

  return (
    <div className={containerClass}>
      <ArrowPathIcon className='h-5 w-5 animate-spin text-sky-600' />
      {label && <PSmallFaded text={label} />}
    </div>
  )
}

export const LoadingSmallCard: React.FC<LoadingProps> = ({ label }) => {
  return (
    <Card className='flex justify-center items-center p-4 text-center flex-col space-y-4'>
      <ArrowPathIcon className='h-5 w-5 animate-spin text-sky-600' />
      {label && <PSmallFaded text={label} />}
    </Card>
  )
}

export const Loading: React.FC<LoadingProps> = ({ label, message, size = 'medium' }) => {
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return {
          container: 'max-w-xs',
          icon: 'h-8 w-8',
          padding: 'px-3 py-4'
        }
      case 'large':
        return {
          container: 'max-w-lg',
          icon: 'h-16 w-16',
          padding: 'px-6 py-8'
        }
      default:
        return {
          container: 'max-w-xs',
          icon: 'h-12 w-12',
          padding: 'px-4 py-5 sm:p-6'
        }
    }
  }

  const sizeClasses = getSizeClasses()

  return (
    <div className={`container mx-auto my-14 ${sizeClasses.container} overflow-hidden rounded-lg bg-white shadow-sm`}>
      <div className={`text-center ${sizeClasses.padding}`}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 24 24'
          fill='currentColor'
          className={`mx-auto my-3 ${sizeClasses.icon} animate-spin text-sky-600`}
        >
          <path
            fillRule='evenodd'
            d='M4.755 10.059a7.5 7.5 0 0112.548-3.364l1.903 1.903h-3.183a.75.75 0 100 1.5h4.992a.75.75 0 00.75-.75V4.356a.75.75 0 00-1.5 0v3.18l-1.9-1.9A9 9 0 003.306 9.67a.75.75 0 101.45.388zm15.408 3.352a.75.75 0 00-.919.53 7.5 7.5 0 01-12.548 3.364l-1.902-1.903h3.183a.75.75 0 000-1.5H2.984a.75.75 0 00-.75.75v4.992a.75.75 0 001.5 0v-3.18l1.9 1.9a9 9 0 0015.059-4.035.75.75 0 00-.53-.918z'
            clipRule='evenodd'
          />
        </svg>

        <Header2 title={label ? 'Loading ' + label : 'Loading'} />
        {message && <PSmallFaded text={message} />}
      </div>
    </div>
  )
}

export const Error: React.FC<ErrorProps> = ({
  title,
  message,
  onRetry,
  retryText = 'Try Again',
  showRetry = true,
  actionable = true
}) => {
  const getActionableMessage = () => {
    if (!actionable || !message) return message

    // Provide actionable guidance for common error patterns
    if (message.includes('Network Error') || message.includes('fetch')) {
      return `${message} Please check your internet connection and try again.`
    }

    if (message.includes('404') || message.includes('not found')) {
      return `${message} The requested resource may have been moved or deleted.`
    }

    if (message.includes('403') || message.includes('unauthorized')) {
      return `${message} You may need to log in again or contact an administrator.`
    }

    if (message.includes('500') || message.includes('server error')) {
      return `${message} This is a temporary server issue. Please try again in a few moments.`
    }

    return message
  }

  return (
    <div className='container mx-auto my-14 max-w-lg overflow-hidden rounded-lg bg-red-50 border border-red-200 shadow-md'>
      <div className='px-4 py-5 text-center sm:p-6'>
        <ExclamationTriangleIcon className='mx-auto my-3 h-12 w-12 text-red-500' />

        <Header2 title={title || 'Error'} className="text-red-800" />

        {getActionableMessage() && (
          <div className="mt-3 space-y-2">
            <PMedium text={getActionableMessage() || ''} className="text-red-700" />
          </div>
        )}

        {onRetry && showRetry && (
          <div className="mt-4">
            <button
              onClick={onRetry}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              {retryText}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Inline loading spinner for buttons and small spaces
 */
export const LoadingSpinner: React.FC<{ size?: 'xs' | 'sm' | 'md' | 'lg' }> = ({ size = 'sm' }) => {
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  }

  return (
    <ArrowPathIcon className={`${sizeClasses[size]} animate-spin text-current`} />
  )
}

/**
 * Skeleton loading component for content placeholders
 */
export const SkeletonLoader: React.FC<{
  lines?: number
  className?: string
  height?: string
}> = ({ lines = 3, className = '', height = 'h-4' }) => {
  return (
    <div className={`animate-pulse space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className={`bg-gray-200 rounded ${height} ${index === lines - 1 ? 'w-3/4' : 'w-full'
            }`}
        />
      ))}
    </div>
  )
}
